import { useCallback, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, Button, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"

import { useOccupantPromotion } from "hooks/promotion/useOccupantPromotion"
import { useSponsoredFooterBanner } from "hooks/sponsored-content/useSponsoredFooterBanner"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { PromotionCard } from "components/domains/promotions"
import { SearchToggleButton } from "components/domains/search"
import { LanguageButton } from "components/Language"
import { Head } from "components/Head"
import { Header, HeaderLogo, HeaderTitle } from "components/Header"
import { Clock } from "components/Clock"
import { AdMediaFooterCarousel } from "components/domains/ads"
import { PercentIcon } from "components/icons"
import { Footer } from "components/Footer"
import { EmptyData } from "components/EmptyData"
import { Carousel, CarouselItem } from "components/Carousel"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"
import { StandardShopPromotionHeader } from "components/domains/promotions/kiosk-v/StandardShopPromotionHeader"
import { ShopPromotionHeader } from "components/domains/promotions/kiosk-v/ShopPromotionHeader"
import { IPageProps } from "pages/types"

export const OccupantPromotionKioskVPage: React.FC<IPageProps> = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const navigate = useNavigate()
  const { goBack, openSearch } = useAppUI()
  const { occupantId } = useParams()
  const { t } = useTranslation()
  const { footerBanners, footerBannerConfig } = useSponsoredFooterBanner()

  const { occupantName, logo, promotions, level, category, hours } =
    useOccupantPromotion(occupantId)

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { defaultProps = {}, styleOverrides = {} } =
    theme.components?.[`OccupantPromotionPage`] || {}
  const { promotionCarouselOption } = defaultProps
  const {
    root,
    carouselContainer,
    carousel,
    carouselItem,
    emptyDataContainer,
    headerContainer,
    actionButtonGroupContainer,
    button,
    actionButton,
    actionContainer,
    searchBarContainer,
    toolbarContainer,
    navigationWrapper,
    promotionCard,
    languageSelectMenu,
  } = styleOverrides
  const slidePerPage = 3
  const carouselType =
    promotions && promotions.length < slidePerPage ? "slide" : "loop"
  const autoplay = promotions && promotions.length > 3
  const focus = promotions.length < 3 ? "center" : promotionCarouselOption.focus

  const { variant } =
    theme.components?.[`ShopPromotionHeader`].defaultProps || {}

  const ShopPromotionHeaderComponent = useMemo(
    () =>
      variant === "standardV1"
        ? StandardShopPromotionHeader
        : ShopPromotionHeader,
    [variant]
  )

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  return (
    <>
      <Head title={t("Promotions")} />
      <Layout
        header={
          <Header
            logo={<HeaderLogo />}
            title={<HeaderTitle />}
            clock={<Clock />}
          />
        }
        footer={
          <>
            {footerBanners.length > 0 ? (
              <AdMediaFooterCarousel
                data={footerBanners}
                carouselOptions={footerBannerConfig}
              />
            ) : (
              <Footer />
            )}
          </>
        }
      >
        <Box sx={root}>
          <Box sx={headerContainer}>
            <ShopPromotionHeaderComponent
              occupantName={occupantName}
              logo={logo}
              level={level}
              category={category}
              hours={hours}
            />
          </Box>
          {!promotions || promotions?.length === 0 ? (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          ) : (
            <>
              <Box sx={carouselContainer}>
                {promotions && promotions.length > 0 ? (
                  <Carousel
                    onClick={handleClickSlide}
                    options={{
                      type: carouselType,
                      pagination: true,
                      autoplay,
                      gap: "0",
                      perPage: slidePerPage,
                      ...promotionCarouselOption,
                      focus,
                    }}
                    sx={carousel}
                  >
                    {promotions.map((promotion) => {
                      const sponsoredId =
                        promotion?.properties?.sponsoredId || ""
                      return (
                        <CarouselItem
                          sx={carouselItem}
                          link={`/promotions/${promotion.id}`}
                          key={`${sponsoredId}${promotion.id}`}
                        >
                          <PromotionCard
                            sx={promotionCard}
                            promotion={promotion}
                          />
                        </CarouselItem>
                      )
                    })}
                  </Carousel>
                ) : (
                  ""
                )}
              </Box>
            </>
          )}
          <Box sx={actionButtonGroupContainer}>
            <Button sx={button} variant="outlined" onClick={goBack}>
              {t("Back")}
            </Button>
          </Box>
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
